import { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, ImageIcon } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const projects = [
  {
    name: "By Milja V",
    industry: "Sisällöntuotanto",
    description:
      "Verkkosivut, jotka esittelevät palvelut ja portfolion sekä ohjaavat kävijän suoraan yhteydenottoon.",
    tags: ["Responsiivinen toteutus", "Hakukoneoptimointi", "Yhteydenottolomake", "Ylläpito"],
    url: "https://bymiljav.fi",
    image: "/images/referenssit/bymiljav.webp",
    imageAlt: "By Milja V -verkkosivuston etusivu",
  },
  {
    name: "Hengähdys Porvoo",
    industry: "Sauna ja tilavuokraus Porvoossa",
    description:
      "Verkkosivut porvoolaiselle saunalle ja tilavuokraukseen: selkeät palvelukuvaukset, hinnasto ja suora varauspolku yhteydenottoon.",
    tags: ["Responsiivinen toteutus", "Varauslomake", "Hakukoneoptimointi", "Ylläpito"],
    url: "https://hengahdysporvoo.fi/",
    image: "/images/referenssit/hengahdysporvoo.webp",
    imageAlt: "Hengähdys Porvoo -verkkosivuston etusivu",
  },
  {
    name: "Ysitien LED",
    industry: "LED- ja ulkomainonta Loimaalla",
    description:
      "Verkkosivut LED-mainospaikkoja tarjoavalle yritykselle: selkeä palvelun esittely, kolme hinnastopakettia ja varauslomake mainospaikkoihin.",
    tags: ["Responsiivinen toteutus", "Hinnastosivu", "Varauslomake", "Hakukoneoptimointi"],
    url: "https://ysitienled.com/",
    image: "/images/referenssit/ysitienled.webp",
    imageAlt: "Ysitien LED -verkkosivuston etusivu",
  },
];

function ProjectImage({ src, alt, name }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return (
      <div
        className="w-full aspect-[16/10] rounded-t-xl grid place-items-center bg-gradient-to-br from-[#0B1236] via-[#1a2560] to-[#1156FE]"
        aria-label={alt}
      >
        <div className="flex flex-col items-center gap-2 text-white/80">
          <ImageIcon size={28} strokeWidth={1.5} />
          <span className="text-xs font-mono uppercase tracking-widest px-4 text-center">{name}</span>
        </div>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full aspect-[16/10] object-cover rounded-t-xl"
      onError={() => setFailed(true)}
    />
  );
}

export default function Testimonials() {
  const onOpen = (p) => (e) => {
    if (!p.url) {
      e.preventDefault();
      return;
    }
    trackEvent("project_click", { company: p.name, url: p.url });
  };

  return (
    <section className="md-section" id="referenssit" data-testid="projects-section">
      <div className="md-container">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-12 md:mb-16"
        >
          <span className="md-eyebrow">Referenssit</span>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Toteutuksemme.
          </h2>
          <p className="mt-5 text-white/70 leading-relaxed text-lg">
            Verkkosivut, jotka palvelevat yritystä joka päivä.
          </p>
        </motion.div>

        <div
          className="grid gap-6 items-stretch grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="projects-grid"
        >
          {projects.map((p, i) => {
            const isLive = Boolean(p.url && !p.url.startsWith("["));
            const CardTag = isLive ? "a" : "div";
            return (
              <motion.article
                key={p.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="h-full"
              >
                <CardTag
                  {...(isLive
                    ? {
                        href: p.url,
                        target: "_blank",
                        rel: "noopener noreferrer",
                        onClick: onOpen(p),
                        "data-track": `project-${p.name}`,
                      }
                    : {})}
                  className="group flex h-full flex-col overflow-hidden rounded-xl border border-[#E2E8F0] bg-white transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(11,18,54,0.25)]"
                  data-testid={`project-card-${i}`}
                >
                  <ProjectImage src={p.image} alt={p.imageAlt} name={p.name} />

                  <div className="flex flex-1 flex-col p-6">
                    <div
                      className="text-xs font-semibold uppercase text-[#1156FE]"
                      style={{ letterSpacing: "0.05em" }}
                    >
                      {p.industry}
                    </div>
                    <h3 className="mt-2 font-display text-xl font-semibold text-[#0B1236]">
                      {p.name}
                    </h3>
                    <p className="mt-3 text-[#4A5568] text-sm" style={{ lineHeight: 1.6 }}>
                      {p.description}
                    </p>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {p.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-[#F1F5F9] px-2.5 py-1 text-xs text-[#334155]"
                        >
                          {t}
                        </span>
                      ))}
                    </div>

                    <div className="mt-6 pt-4 border-t border-[#E2E8F0] mt-auto">
                      {isLive ? (
                        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1156FE] group-hover:gap-2 transition-all">
                          Katso sivusto <ExternalLink size={14} />
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#94A3B8]">
                          Sivusto tulossa
                        </span>
                      )}
                    </div>
                  </div>
                </CardTag>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
